import { ReactNode, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, Users, Sparkles, Trophy, Video, Menu, User, Flame, BookMarked, Radio, Facebook } from "lucide-react";
import AvatarMenu from "@/components/layout/AvatarMenu";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import {
  isNavItemActive,
  sectionsForAuth,
  SHEET_SECTIONS,
} from "@/components/layout/nav-config";
import { HeaderSearch } from "@/components/layout/HeaderSearch";
import { useAuth } from "@/hooks/use-auth";
import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { useGamification } from "@/hooks/use-gamification";
import { useGamificationUIStore } from "@lyder-talk/core";
import { StreakDetailModal } from "@/components/gamification";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TopicGenerationStatus } from "@/components/topic/TopicGenerationStatus";
import { useTheme } from "@lyder-talk/core";
import { useTranslation } from "react-i18next";
import { useNavLabels } from "@/components/layout/useNavLabels";
import { cn } from "@/lib/utils";
import { useGuardedNavigate } from "@/hooks/use-guarded-navigate";

export interface PageHeaderProps {
  leftContent?: ReactNode;
  className?: string;
}

export const PageHeader = ({
  leftContent,
  className = "",
}: PageHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Routes that render their own search field: the header keeps the brand and
  // the actions, and stops offering a second box for the same query.
  const ownsSearch = location.pathname === "/topics" || location.pathname.startsWith("/topics/");
  const navLabels = useNavLabels();
  const { data: isAuthenticated, userId } = useAuth();
  const { requireAuth } = useAuthRequired();
  const guardedNavigate = useGuardedNavigate();
  const { level, xp, currentStreak, levelProgress, isLoading: isGamificationLoading } = useGamification();

  const { theme, resolvedTheme, setTheme } = useTheme();
  const { setStreakModalOpen } = useGamificationUIStore();

  /**
   * The drawer's sections come straight from `nav-config`, filtered to the
   * items flagged `inSheet`. This list used to be hand-maintained here with
   * different labels ("Browse Topics" vs the sidebar's "Browse") and different
   * membership, which is exactly the drift the config removes.
   */
  const sheetSections = sectionsForAuth(SHEET_SECTIONS, isAuthenticated);

  /**
   * Chrome's Sign In opens the gate on its sign-in screen rather than leaving
   * for `/auth/signin`. The visitor has already decided to sign in, so the
   * gate's reason screen would be a detour — and a full page load in between
   * costs them the page they were on.
   */
  const handleSignIn = () => {
    void requireAuth({ stage: "signin" });
  };

  return (
    <div className={`flex items-center justify-between gap-1 md:gap-3 py-1.5 md:py-2 ${className}`}>
      {/* Left Section: Logo + Navigation */}
      <div className="flex items-center gap-0.5 md:gap-1.5">
        {/* Mobile Hamburger */}
        <div className="flex md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-lg text-muted-foreground hover:text-foreground">
                <Menu className="h-5 w-5" />
                <span className="sr-only">{t("nav.header.menu", "Toggle menu")}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[80%] max-w-[320px] bg-white dark:bg-stone-900 border-r-stone-200 dark:border-r-stone-700 [&>button]:hidden flex flex-col !top-0 !inset-y-0 !h-full !max-h-none !rounded-none">
              <SheetHeader>
                <SheetTitle className="text-left flex items-center gap-2 mb-6">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
                    <img
                      src="/icon.png"
                      alt="Lyder Talk"
                      className="w-9 h-9 rounded-xl"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="text-base font-bold text-primary-strong dark:text-primary">
                      Lyder Talk
                    </span>
                  </div>
                </SheetTitle>
              </SheetHeader>
              <ScrollArea className="flex-1 min-h-0 -mx-2 px-2">
                <div className="flex flex-col justify-between min-h-full gap-0.5 pb-8">
                  {sheetSections.map((section, sectionIndex) => (
                    <div key={section.id}>
                      <div
                        className={`px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-stone-500 dark:text-stone-400 ${
                          sectionIndex === 0 ? "pt-2 pb-1.5" : "pt-6 pb-1.5"
                        }`}
                      >
                        {navLabels.sectionLabel(section)}
                      </div>
                      {section.items.map((item) => {
                        const isActive = isNavItemActive(location.pathname, item);
                        const Icon = item.icon;
                        return (
                          <SheetClose asChild key={item.id}>
                            <Button
                              variant="ghost"
                              className={cn(
                                "h-14 justify-start gap-0 rounded-xl text-sm",
                                isActive
                                  ? "bg-primary/10 font-semibold text-primary"
                                  : "text-stone-600 hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-white"
                              )}
                              aria-current={isActive ? "page" : undefined}
                              onClick={() => void guardedNavigate(item.path)}
                            >
                              <div
                                className={cn(
                                  "mr-3.5 flex h-9 w-9 items-center justify-center rounded-xl",
                                  isActive
                                    ? "bg-primary/20 text-primary"
                                    : "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400"
                                )}
                              >
                                <Icon className={cn("h-[18px] w-[18px]", item.iconClassName)} aria-hidden="true" />
                              </div>
                              <span>{navLabels.itemLabel(item)}</span>
                            </Button>
                          </SheetClose>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {!isAuthenticated && (
                <div className="p-4 border-t border-stone-200 dark:border-stone-700 mt-auto shrink-0">
                  <Button
                    className="w-full h-12 text-base bg-primary-strong hover:bg-primary-strong/90 shadow-md shadow-primary/25 text-primary-foreground"
                    onClick={handleSignIn}
                  >
                    <LogIn className="w-5 h-5 mr-2" />
                    {t("common.actions.signIn", "Sign In")}
                  </Button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
        {/* Logo — mobile only; desktop brand lives in the left sidebar */}
        <div className="flex md:hidden items-center gap-2 cursor-pointer pr-0.5" onClick={() => navigate("/")}>
          <img
            src="/icon.png"
            alt="Lyder Talk"
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl transition-transform duration-200 hover:scale-105 active:scale-95"
            loading="eager"
          />
        </div>

        {leftContent}

        {/* Desktop: primary nav lives in the left sidebar — header keeps search only */}
        <div className="hidden md:flex items-center gap-0.5 flex-1 min-w-0 max-w-xl">
          <HeaderSearch />
        </div>
      </div>

      {/* Right Section: Auth-dependent buttons */}
      <div className="hidden md:flex items-center gap-1">
        {isAuthenticated ? (
          <>
            <TopicGenerationStatus variant="persistent" />
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 rounded-lg px-2.5 h-9 bg-facebook/10 text-facebook hover:bg-facebook/20 transition-all duration-200"
              asChild
            >
              <a
                href="https://www.facebook.com/lydertalk"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook Community"
              >
                <Facebook className="w-4 h-4" />
                <span className="hidden lg:inline text-sm">Community</span>
              </a>
            </Button>
            <NotificationBell userId={userId} />
            {!isGamificationLoading && currentStreak > 0 && (
              <button
                type="button"
                onClick={() => setStreakModalOpen(true)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-500/20 transition-all duration-200 cursor-pointer"
              >
                <Flame className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">{currentStreak}</span>
                <span className="hidden sm:inline text-[11px] font-medium">day{currentStreak !== 1 ? "s" : ""}</span>
              </button>
            )}
            <AvatarMenu
              gamificationProps={{
                level,
                xp,
                currentStreak,
                levelProgress,
                isLoading: isGamificationLoading,
              }}
              themeProps={{ theme, resolvedTheme, setTheme }}
              onStreakClick={() => setStreakModalOpen(true)}
            />
          </>
        ) : (
          <Button
            variant="default"
            size="sm"
            onClick={handleSignIn}
            className="gap-1.5 rounded-lg h-9 bg-primary-strong hover:bg-primary-strong/90 transition-all duration-200 shadow-md shadow-primary/25 px-3 text-sm font-medium text-primary-foreground"
            aria-label={t("common.actions.signIn", "Sign In")}
          >
            <LogIn className="w-4 h-4" />
            <span>{t("common.actions.signIn", "Sign In")}</span>
          </Button>
        )}
      </div>

      {/* Mobile Navigation */}
      <div className="flex md:hidden items-center gap-1 flex-1 min-w-0">
        {/* Browse renders its own search field, which live-filters the list.
            Two identical placeholder boxes 300px apart, one of which needs
            Enter, is a coin flip for a one-handed user. The page's field wins
            on the routes that have one. */}
        {!ownsSearch && <HeaderSearch className="flex-1 min-w-0" />}

        <div className="flex items-center gap-0.5 shrink-0">
          {isAuthenticated ? (
            <>
              <div className="hidden sm:flex items-center gap-0.5">
                <TopicGenerationStatus variant="persistent" />
              </div>
              <a
                href="https://www.facebook.com/lydertalk"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center h-9 w-9 rounded-lg bg-facebook/10 text-facebook hover:bg-facebook/20 transition-all duration-200"
                aria-label="Facebook Community"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <NotificationBell userId={userId} />
              <AvatarMenu
                gamificationProps={{
                  level,
                  xp,
                  currentStreak,
                  levelProgress,
                  isLoading: isGamificationLoading,
                }}
                themeProps={{ theme, resolvedTheme, setTheme }}
                onStreakClick={() => setStreakModalOpen(true)}
              />
            </>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={handleSignIn}
              className="gap-1 rounded-lg h-9 bg-primary-strong hover:bg-primary-strong/90 transition-all duration-200 shadow-md shadow-primary/25 px-2.5 text-xs font-medium text-primary-foreground"
            >
              <LogIn className="w-4 h-4" />
              <span>{t("common.actions.signIn", "Sign In")}</span>
            </Button>
          )}
        </div>
      </div>

      {/* The streak dialog used to be mounted here too, reading the same global
          store as every other chrome. On a page that rendered this header *and*
          a route body that also mounted one, two dialogs opened at once. It is
          mounted once, above the routes, in `StreakDetailHost`. */}
    </div>
  );
};

export interface StickyPageHeaderProps extends PageHeaderProps {
  containerClassName?: string;
  wrapperClassName?: string;
}

export const StickyPageHeader = ({
  containerClassName = "container mx-auto px-2",
  wrapperClassName = "sticky top-0 z-50 bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-700",
  ...pageHeaderProps
}: StickyPageHeaderProps) => {
  return (
    <div className={wrapperClassName}>
      <div className={containerClassName}>
        <PageHeader {...pageHeaderProps} />
      </div>
    </div>
  );
};
